import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../setup/redux/hooks';

// Redundant attribute names that are already handled in other wizard steps
const REDUNDANT_ATTRIBUTE_NAMES = [
  // STEP 1: Basic Info - Product Identifier
  'sku', 'SKU', 'product_sku', 'productSku', 'item_sku', 'itemSku',
  'identifier', 'Identifier', 'product_identifier', 'productIdentifier',
  'product_id', 'productId', 'item_id', 'itemId', 'code', 'Code',
  
  // STEP 1: Basic Info - Product Details
  'product_name', 'productName', 'name', 'Name', 'title', 'Title',
  'product_title', 'productTitle', 'item_name', 'itemName',
  'product_description', 'productDescription', 'description', 'Description',
  'summary', 'Summary', 'overview', 'Overview', 'details', 'Details',
  'product_details', 'productDetails', 'item_description', 'itemDescription',
  'video_url', 'videoUrl', 'video', 'Video', 'video_link', 'videoLink',
  'product_video', 'productVideo', 'promotional_video', 'promotionalVideo',
  'product_images', 'productImages', 'images', 'Images', 'photos', 'Photos',
  'image', 'Image', 'photo', 'Photo', 'picture', 'Picture',
  
  // STEP 2: Classification - Already covered in ClassificationStep
  'brand', 'Brand', 'product_brand', 'productBrand', 'manufacturer', 'Manufacturer',
  'brand_name', 'brandName', 'product_manufacturer', 'productManufacturer',
  'category', 'Category', 'product_category', 'productCategory', 'category_name', 'categoryName',
  'license', 'License', 'license_number', 'licenseNumber', 'certification', 'Certification',
  'license_document', 'licenseDocument', 'certificate', 'Certificate', 'permit', 'Permit',
  'registration', 'Registration', 'approval', 'Approval',
  
  // STEP 3: Sales Info - Pricing
  'price', 'Price', 'product_price', 'productPrice', 'regular_price', 'regularPrice',
  'sale_price', 'salePrice', 'selling_price', 'sellingPrice', 'retail_price', 'retailPrice',
  'cost_price', 'costPrice', 'cost', 'Cost', 'wholesale_price', 'wholesalePrice',
  'currency', 'Currency', 'pricing', 'Pricing', 'amount', 'Amount',
  
  // STEP 3: Sales Info - Stock Management
  'stock', 'Stock', 'quantity', 'Quantity', 'total_stock', 'totalStock',
  'stock_quantity', 'stockQuantity', 'inventory', 'Inventory', 'qty', 'QTY',
  'available_stock', 'availableStock', 'warehouse_stock', 'warehouseStock',
  'min_order', 'minOrder', 'minimum_order', 'minimumOrder', 'moq', 'MOQ',
  
  // STEP 3: Sales Info - Variants
  'variant', 'Variant', 'variants', 'Variants', 'variation', 'Variation',
  'product_variants', 'productVariants', 'variant_options', 'variantOptions',
  'variant_combinations', 'variantCombinations', 'options', 'Options',
  'color', 'Color', 'size', 'Size', 'material', 'Material', 'style', 'Style',
  'variant_sku', 'variantSku', 'variant_price', 'variantPrice',
  
  // STEP 3: Sales Info - Tags & Promotion
  'tags', 'Tags', 'product_tags', 'productTags', 'labels', 'Labels',
  'promotion', 'Promotion', 'promotional_tags', 'promotionalTags',
  'keywords', 'Keywords', 'search_tags', 'searchTags',
  
  // STEP 4: Shipping - Package Dimensions
  'weight', 'Weight', 'package_weight', 'packageWeight', 'shipping_weight', 'shippingWeight',
  'length', 'Length', 'package_length', 'packageLength', 'dimension_length', 'dimensionLength',
  'width', 'Width', 'package_width', 'packageWidth', 'dimension_width', 'dimensionWidth',
  'height', 'Height', 'package_height', 'packageHeight', 'dimension_height', 'dimensionHeight',
  'dimensions', 'Dimensions', 'package_dimensions', 'packageDimensions',
  'shipping_dimensions', 'shippingDimensions', 'product_dimensions', 'productDimensions',
  
  // STEP 4: Shipping - Safety & Handling
  'dangerous', 'Dangerous', 'is_dangerous', 'isDangerous', 'hazardous', 'Hazardous',
  'fragile', 'Fragile', 'is_fragile', 'isFragile', 'handling', 'Handling',
  'packing_instructions', 'packingInstructions', 'special_handling', 'specialHandling',
  
  // STEP 4: Shipping - Logistics
  'shipping', 'Shipping', 'delivery', 'Delivery', 'logistics', 'Logistics',
  'shipping_company', 'shippingCompany', 'courier', 'Courier', 'carrier', 'Carrier',
  'shipping_method', 'shippingMethod', 'delivery_method', 'deliveryMethod',
  'shipping_service', 'shippingService', 'delivery_service', 'deliveryService',
  'pre_order', 'preOrder', 'processing_days', 'processingDays', 'lead_time', 'leadTime'
];

/**
 * Check if an attribute should be filtered out because it's redundant
 * with fields already present in other wizard steps
 */
const isRedundantAttribute = (attr: AccountCategoryAttribute): boolean => {
  const originalName = attr.originalAttributeName?.toLowerCase() || '';
  const displayName = attr.displayAttributeName?.toLowerCase() || '';
  
  // Check for exact matches first (most reliable)
  const hasExactMatch = REDUNDANT_ATTRIBUTE_NAMES.some(redundantName => {
    const lowerRedundant = redundantName.toLowerCase();
    return originalName === lowerRedundant || displayName === lowerRedundant;
  });
  
  if (hasExactMatch) {
    return true;
  }
  
  // Check for partial matches (more lenient, but still specific)
  const hasPartialMatch = REDUNDANT_ATTRIBUTE_NAMES.some(redundantName => {
    const lowerRedundant = redundantName.toLowerCase();
    // Only filter if the attribute name contains the redundant word as a complete word
    // This prevents over-filtering (e.g., "color_code" won't match "color" unless it's "color" exactly)
    const wordBoundaryPattern = new RegExp(`\\b${lowerRedundant}\\b`);
    return wordBoundaryPattern.test(originalName) || wordBoundaryPattern.test(displayName);
  });
  
  return hasPartialMatch;
};
import {
  fetchAccountCategories,
  fetchAccountCategoryAttributes,
  searchAccountCategories,
  fetchAllAccountCategories,
  fetchCategoriesByParent,
  clearAccountCategories,
  clearAccountCategoriesForMarketplace,
  clearAccountCategoryError,
  clearAccountCategoryAttributesError,
  selectAccountCategoriesByMarketplace,
  selectAccountCategoriesLoading,
  selectAccountCategoriesError,
  selectAccountCategoryAttributesByCategoryId,
  selectAccountCategoryAttributesLoadingByCategoryId,
  selectAccountCategoryAttributesErrorByCategoryId,
  selectIsAccountCategoriesDataStale,
  selectAccountCategoriesAsOptions,
  selectCategoriesByParent,
  selectCategoriesByParentLoading,
  selectCategoriesByParentError,
  selectHasCategoriesByParent,
} from '../redux/accountCategorySlice';
import type {
  MarketplaceType,
  AccountCategory,
  AccountCategoryAttribute,
  AccountCategoryOption,
} from '../types/AccountCategory';
import type { RootState } from '../../../../setup/redux/store';

/**
 * Custom hook for account category operations
 * Provides a convenient interface for components to interact with account categories
 */
export const useAccountCategories = (marketplace?: MarketplaceType) => {
  const dispatch = useAppDispatch();

  // Selectors
  const categories = useSelector(selectAccountCategoriesByMarketplace(marketplace));
  const loading = useSelector(selectAccountCategoriesLoading);
  const error = useSelector(selectAccountCategoriesError);
  const categoriesAsOptions = useSelector(selectAccountCategoriesAsOptions(marketplace));
  const isStale = useSelector(selectIsAccountCategoriesDataStale(marketplace));

  // Actions
  const fetchCategories = useCallback(
    async (options?: {
      search?: string;
      page?: number;
      limit?: number;
      useCache?: boolean;
    }) => {
      if (!marketplace) {
        throw new Error('Marketplace is required to fetch categories');
      }

      return dispatch(
        fetchAccountCategories({
          marketplace,
          ...options,
        }) as any
      );
    },
    [dispatch, marketplace]
  );

  const searchCategories = useCallback(
    async (searchTerm: string, targetMarketplaces?: MarketplaceType[]) => {
      const marketplaces = targetMarketplaces || (marketplace ? [marketplace] : ['shopee', 'lazada', 'tiktok']);
      
      return dispatch(
        searchAccountCategories({
          searchTerm,
          marketplaces,
        }) as any
      );
    },
    [dispatch, marketplace]
  );

  const fetchAllCategories = useCallback(
    async (search?: string) => {
      if (!marketplace) {
        throw new Error('Marketplace is required to fetch all categories');
      }

      return dispatch(
        fetchAllAccountCategories({
          marketplace,
          search,
        }) as any
      );
    },
    [dispatch, marketplace]
  );

  const clearCategories = useCallback(() => {
    if (marketplace) {
      dispatch(clearAccountCategoriesForMarketplace(marketplace));
    } else {
      dispatch(clearAccountCategories());
    }
  }, [dispatch, marketplace]);

  const clearError = useCallback(() => {
    dispatch(clearAccountCategoryError());
  }, [dispatch]);

  // Utility functions
  const refreshCategories = useCallback(
    async (options?: { search?: string }) => {
      if (!marketplace) return;

      return fetchCategories({
        ...options,
        useCache: false, // Force refresh
      });
    },
    [fetchCategories, marketplace]
  );

  const getCategoryById = useCallback(
    (categoryId: string): AccountCategory | undefined => {
      return categories.find(cat => cat.id === categoryId);
    },
    [categories]
  );

  const getCategoriesBySearch = useCallback(
    (searchTerm: string): AccountCategory[] => {
      if (!searchTerm) return categories;
      
      const term = searchTerm.toLowerCase();
      return categories.filter(cat =>
        cat.name.toLowerCase().includes(term) ||
        cat.id.toLowerCase().includes(term)
      );
    },
    [categories]
  );

  // Auto-fetch ALL categories if marketplace is set and data is stale
  const ensureCategoriesLoaded = useCallback(
    async (force = false) => {
      if (!marketplace) return;

      if (force || categories.length === 0 || isStale) {
        // Use fetchAllCategories to get ALL pages for tree building
        await fetchAllCategories();
      }
    },
    [marketplace, categories.length, isStale, fetchAllCategories]
  );

  // Fetch root categories (parentId = null)
  const fetchRootCategories = useCallback(
    async () => {
      if (!marketplace) return;
      return dispatch(fetchCategoriesByParent({ marketplace, parentId: null }) as any);
    },
    [dispatch, marketplace]
  );

  // Fetch child categories for a parent
  const fetchChildCategories = useCallback(
    async (parentId: string) => {
      if (!marketplace) return;
      return dispatch(fetchCategoriesByParent({ marketplace, parentId }) as any);
    },
    [dispatch, marketplace]
  );

  return {
    // Data
    categories,
    categoriesAsOptions,
    loading,
    error,
    isStale,

    // Actions
    fetchCategories,
    searchCategories,
    fetchAllCategories,
    fetchRootCategories,
    fetchChildCategories,
    refreshCategories,
    clearCategories,
    clearError,
    ensureCategoriesLoaded,

    // Utilities
    getCategoryById,
    getCategoriesBySearch,

    // Status
    hasCategories: categories.length > 0,
    isEmpty: !loading && categories.length === 0,
  };
};

/**
 * Hook for managing category attributes
 */
export const useAccountCategoryAttributes = (categoryId?: string) => {
  const dispatch = useAppDispatch();

  // Selectors
  const attributes = useSelector(selectAccountCategoryAttributesByCategoryId(categoryId));
  const loading = useSelector(selectAccountCategoryAttributesLoadingByCategoryId(categoryId));
  const error = useSelector(selectAccountCategoryAttributesErrorByCategoryId(categoryId));

  // Actions
  const fetchAttributes = useCallback(
    async (marketplace: string, targetCategoryId?: string) => {
      const id = targetCategoryId || categoryId;
      if (!id) {
        throw new Error('Category ID is required to fetch attributes');
      }

      return dispatch(
        fetchAccountCategoryAttributes({
          marketplace,
          categoryId: id,
        }) as any
      );
    },
    [dispatch, categoryId]
  );

  const clearAttributesError = useCallback(() => {
    if (categoryId) {
      dispatch(clearAccountCategoryAttributesError(categoryId));
    }
  }, [dispatch, categoryId]);

  // Utility functions
  const getRequiredAttributes = useCallback((): AccountCategoryAttribute[] => {
    return attributes.filter(attr => attr.isMandatory);
  }, [attributes]);

  const getOptionalAttributes = useCallback((): AccountCategoryAttribute[] => {
    return attributes.filter(attr => !attr.isMandatory);
  }, [attributes]);

  const getAttributeById = useCallback(
    (attributeId: number): AccountCategoryAttribute | undefined => {
      return attributes.find(attr => attr.attributeId === attributeId);
    },
    [attributes]
  );

  const getDropdownAttributes = useCallback((): AccountCategoryAttribute[] => {
    return attributes.filter(attr => 
      attr.inputType === 'DROP_DOWN' && 
      attr.attributeValues && 
      attr.attributeValues.length > 0
    );
  }, [attributes]);

  const getTextAttributes = useCallback((): AccountCategoryAttribute[] => {
    return attributes.filter(attr => attr.inputType === 'TEXT_FIELD');
  }, [attributes]);

  const getSortedAttributes = useCallback((): AccountCategoryAttribute[] => {
    return attributes
      .slice() // Create a copy to avoid mutating the original array
      .filter(attr => !isRedundantAttribute(attr)) // Filter out redundant attributes
      .sort((a, b) => {
        // Sort by mandatory status first (mandatory attributes first)
        if (a.isMandatory && !b.isMandatory) return -1;
        if (!a.isMandatory && b.isMandatory) return 1;
        
        // Then sort by display name alphabetically
        const aName = a.displayAttributeName || a.originalAttributeName || '';
        const bName = b.displayAttributeName || b.originalAttributeName || '';
        return aName.toLowerCase().localeCompare(bName.toLowerCase());
      });
  }, [attributes]);

  return {
    // Data
    attributes,
    loading,
    error,
    
    // Actions
    fetchAttributes,
    clearAttributesError,
    
    // Utilities
    getRequiredAttributes,
    getOptionalAttributes,
    getAttributeById,
    getDropdownAttributes,
    getTextAttributes,
    getSortedAttributes,
    
    // Status
    hasAttributes: attributes.length > 0,
    isEmpty: !loading && attributes.length === 0,
    hasRequiredAttributes: getRequiredAttributes().length > 0,
    hasOptionalAttributes: getOptionalAttributes().length > 0,
  };
};

/**
 * Hook for account category search functionality
 */
export const useAccountCategorySearch = () => {
  const dispatch = useAppDispatch();
  const loading = useSelector(selectAccountCategoriesLoading);
  const error = useSelector(selectAccountCategoriesError);

  const searchAcrossMarketplaces = useCallback(
    async (searchTerm: string, marketplaces?: MarketplaceType[]) => {
      if (!searchTerm.trim()) {
        throw new Error('Search term cannot be empty');
      }

      return dispatch(
        searchAccountCategories({
          searchTerm,
          marketplaces: marketplaces || ['shopee', 'lazada', 'tiktok'],
        }) as any
      );
    },
    [dispatch]
  );

  const searchInMarketplace = useCallback(
    async (marketplace: MarketplaceType, searchTerm: string) => {
      return dispatch(
        fetchAccountCategories({
          marketplace,
          search: searchTerm,
          limit: 100,
        }) as any
      );
    },
    [dispatch]
  );

  return {
    // Actions
    searchAcrossMarketplaces,
    searchInMarketplace,
    
    // Status
    loading,
    error,
  };
};

/**
 * Hook for hierarchical category loading (lazy load by parent)
 * Use this for CategoryBrowser component to load categories level by level
 */
export const useHierarchicalCategories = (marketplace?: MarketplaceType) => {
  const dispatch = useAppDispatch();

  // Get categories by parent from Redux state
  const getCategoriesByParentId = useCallback(
    (parentId: string | null) => {
      if (!marketplace) return [];
      return selectCategoriesByParent(marketplace, parentId);
    },
    [marketplace]
  );

  // Check if categories for a parent are loading
  const isLoadingByParent = useCallback(
    (parentId: string | null) => {
      if (!marketplace) return false;
      return selectCategoriesByParentLoading(marketplace, parentId);
    },
    [marketplace]
  );

  // Check if categories for a parent have error
  const getErrorByParent = useCallback(
    (parentId: string | null) => {
      if (!marketplace) return null;
      return selectCategoriesByParentError(marketplace, parentId);
    },
    [marketplace]
  );

  // Check if categories for a parent are already loaded
  const hasCategoriesForParent = useCallback(
    (parentId: string | null) => {
      if (!marketplace) return false;
      return selectHasCategoriesByParent(marketplace, parentId);
    },
    [marketplace]
  );

  // Fetch categories for a parent (root if null)
  const fetchForParent = useCallback(
    async (parentId: string | null) => {
      if (!marketplace) return;
      return dispatch(fetchCategoriesByParent({ marketplace, parentId }) as any);
    },
    [dispatch, marketplace]
  );

  // Ensure categories are loaded for a parent (skip if already loaded)
  const ensureLoadedForParent = useCallback(
    async (parentId: string | null) => {
      if (!marketplace) return;
      // This needs to be called with current state, so we dispatch and let reducer handle
      return dispatch(fetchCategoriesByParent({ marketplace, parentId }) as any);
    },
    [dispatch, marketplace]
  );

  return {
    getCategoriesByParentId,
    isLoadingByParent,
    getErrorByParent,
    hasCategoriesForParent,
    fetchForParent,
    ensureLoadedForParent,
    marketplace,
  };
};

export default useAccountCategories;